import logger from '@/lib/logger';
import { Box, Button, Typography } from '@mui/material';
import { VideoPlayer } from '../VideoPlayer';
import { BaselineReviewOverlay } from '../BaselineReviewOverlay';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { useDeviceStore } from '@/store/useDeviceStore';
import { useUser } from '@/store/useAuthStore';
import { features } from '@/config/features';
import { useState } from 'react';

/**
 * Step 5: Apply Specimen to Electrode
 *
 * Final instruction step before measurement begins. On "Start the test",
 * records specimenStartedAt, marks measurementInFlight as 'specimen', and
 * advances currentStep to 6 (Incubation) — all within the wizard shell.
 */
export function Step5ApplySpecimen() {
  const testId = useMeasurementWizardStore((s) => s.testId);
  const sampleCode = useMeasurementWizardStore((s) => s.sampleCode);
  const baselineResult = useMeasurementWizardStore((s) => s.baselineResult);
  const baselineCompletedAt = useMeasurementWizardStore((s) => s.baselineCompletedAt);
  const setSpecimenStartedAt = useMeasurementWizardStore((s) => s.setSpecimenStartedAt);
  const setMeasurementInFlight = useMeasurementWizardStore((s) => s.setMeasurementInFlight);
  const setCurrentStep = useMeasurementWizardStore((s) => s.setCurrentStep);
  const deviceUid = useDeviceStore((s) => s.deviceInfo?.uid ?? '');
  const user = useUser();
  const [error, setError] = useState<string | null>(null);
  const [showReview, setShowReview] = useState(false);

  const handleStartTest = () => {
    if (!testId) {
      setError('No test ID available. Please restart the wizard.');
      return;
    }
    // Record the moment the user clicked — this is the true incubation start.
    // useSpecimenMeasurement uses specimenStartedAt to compute elapsed incubation
    // time so the 2-min countdown begins now, not from when the baseline finished.
    setSpecimenStartedAt(new Date());
    setMeasurementInFlight({ phase: 'incubating', deviceUid });
    logger.debug('[Step5] Advancing to incubation (step 6), testId:', testId);
    setCurrentStep(6);
  };

  return (
    <>
    <Box sx={{ maxWidth: 600, mx: 'auto', py: 0 }}>
      <Typography variant="h5" gutterBottom align="center">
        Upper Respiratory Infection Panel
      </Typography>

      <Box sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <VideoPlayer
          src="/videos/step-5-apply-specimen.mp4"
          poster="/videos/step-5-poster.jpg"
          controls
          autoPlay
        />
      </Box>

      <Typography variant="body1" sx={{ mb: 4 }}>
        Close the lid on the tube, and then carefully squeeze two
        drops of the sample solution into the test&apos;s well.
      </Typography>

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          Error: {error}
        </Typography>
      )}

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleStartTest}
        disabled={!testId}
      >
        Start the test
      </Button>

      {/* Baseline review — Admin/Researcher only */}
      {baselineCompletedAt !== null &&
        baselineResult !== null &&
        features.enableResearchFeatures &&
        (user?.roles?.includes('researcher') || user?.roles?.includes('admin')) && (
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() => setShowReview(true)}
            sx={{ mt: 1 }}
          >
            Review Baseline
          </Button>
        )}
    </Box>

    {/* Baseline Review full-screen overlay */}
    {features.enableResearchFeatures && baselineResult !== null && baselineCompletedAt !== null && (
      <BaselineReviewOverlay
        open={showReview}
        testId={testId}
        sampleCode={sampleCode}
        baselineResult={baselineResult}
        baselineCompletedAt={baselineCompletedAt}
        onProceed={() => setShowReview(false)}
        onDiscard={() => setShowReview(false)}
      />
    )}
    </>
  );
}
